/** Overview "Usage this month" card — feature usage vs. quota from
 * /waf/billing/feature-usage, with a last/current-month toggle. */
import { useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardBody, SegmentedControl, Skeleton } from '@vnetwork/ui/primitives';
import { useWafFeatureUsage } from '@/api/waf';
import { fmtBytes, fmtNum, monthRange } from './format';
import styles from './WafDashboard.module.css';

/** A labelled usage-vs-quota progress row. */
function QuotaBar({ label, usage, quota, format }: { label: string; usage: number; quota: number; format: (n: number) => string }) {
  const pct = quota > 0 ? Math.min(100, (usage / quota) * 100) : 0;
  const over = quota > 0 && usage > quota;
  return (
    <div>
      <div className={styles.quotaRow}>
        <span className={styles.quotaLabel}>{label}</span>
        <span className={styles.quotaQuota}>{format(quota)}</span>
      </div>
      <div className={styles.bar}>
        <div className={`${styles.barFill} ${over ? styles.barFillOver : ''}`} style={{ width: `${over ? 100 : pct}%` }} />
      </div>
      <span className={styles.quotaUsage}>{format(usage)}</span>
    </div>
  );
}

export function QuotaCard({ serviceId }: { serviceId: string }) {
  const [month, setMonth] = useState<'last' | 'current'>('current');
  const range = useMemo(() => monthRange(month), [month]);
  const q = useWafFeatureUsage(serviceId, range.start_time, range.end_time);
  const u = q.data;
  return (
    <Card>
      <CardHeader
        actions={
          <SegmentedControl
            items={[{ value: 'last', label: 'Last' }, { value: 'current', label: 'Current' }]}
            value={month}
            onValueChange={(v) => setMonth(v as 'last' | 'current')}
            size="sm"
            aria-label="Billing month"
          />
        }
      >
        <CardTitle>Usage this month</CardTitle>
      </CardHeader>
      <CardBody>
        {q.isPending ? (
          <Skeleton style={{ height: 180 }} />
        ) : !u ? (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 13 }}>No usage data.</span>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <QuotaBar label="CDN Traffic" usage={u.cdn_traffic_usage} quota={u.cdn_traffic_quota} format={fmtBytes} />
            <QuotaBar label="CDN Requests" usage={u.cdn_request_usage} quota={u.cdn_request_quota} format={fmtNum} />
            <QuotaBar label="DNS Query" usage={u.dns_query_usage} quota={u.dns_query_quota} format={fmtNum} />
            <QuotaBar label="MCDN Integration" usage={u.mcdn_integration_usage} quota={u.mcdn_integration_quota} format={(n) => String(n)} />
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default QuotaCard;
